import React, { useEffect, useState } from 'react'
import { HiPlus } from 'react-icons/hi2'
import { serviceProviderService } from '../services/serviceProviderService'
import { PageHeader } from '../components/ui/PageHeader'
import { DataTable } from '../components/ui/DataTable'
import { Button } from '../components/ui/Button'
import { Modal } from '../components/ui/Modal'
import { Input } from '../components/ui/Input'
import { ConfirmDialog } from '../components/ui/ConfirmDialog'

const ServiceProviders = () => {
  const [providers, setProviders] = useState([])
  const [loading, setLoading] = useState(true)
  const [showCreateModal, setShowCreateModal] = useState(false)
  const [showEditModal, setShowEditModal] = useState(false)
  const [selectedProvider, setSelectedProvider] = useState(null)
  const [createLoading, setCreateLoading] = useState(false)
  const [editLoading, setEditLoading] = useState(false)
  const [createForm, setCreateForm] = useState({ provider_code: '', provider_name: '' })
  const [editForm, setEditForm] = useState({ provider_name: '' })
  const [deleteTarget, setDeleteTarget] = useState(null)
  const [deleteLoading, setDeleteLoading] = useState(false)

  const fetchProviders = async () => {
    setLoading(true)
    try {
      const response = await serviceProviderService.getProviders()
      if (response.success) setProviders(response.data.providers || [])
      else setProviders([])
    } catch {
      setProviders([])
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchProviders()
  }, [])

  const handleCreate = async (e) => {
    e.preventDefault()
    setCreateLoading(true)
    try {
      await serviceProviderService.createProvider({
        provider_code: createForm.provider_code,
        provider_name: createForm.provider_name,
      })
      setShowCreateModal(false)
      setCreateForm({ provider_code: '', provider_name: '' })
      await fetchProviders()
    } finally {
      setCreateLoading(false)
    }
  }

  const handleOpenEdit = (p) => {
    setSelectedProvider(p)
    setEditForm({ provider_name: p.provider_name || '' })
    setShowEditModal(true)
  }

  const handleUpdate = async (e) => {
    e.preventDefault()
    if (!selectedProvider?.provider_code) return
    setEditLoading(true)
    try {
      await serviceProviderService.updateProvider(selectedProvider.provider_code, {
        provider_name: editForm.provider_name,
      })
      setShowEditModal(false)
      setSelectedProvider(null)
      await fetchProviders()
    } finally {
      setEditLoading(false)
    }
  }

  const handleConfirmDelete = async () => {
    if (!deleteTarget) return
    setDeleteLoading(true)
    try {
      await serviceProviderService.deleteProvider(deleteTarget)
      setDeleteTarget(null)
      await fetchProviders()
    } finally {
      setDeleteLoading(false)
    }
  }

  const columns = [
    {
      key: 'provider_code',
      header: 'Code',
      render: (p) => <span className="font-mono font-medium">{p.provider_code}</span>,
    },
    { key: 'provider_name', header: 'Name' },
    {
      key: 'actions',
      header: 'Actions',
      className: 'text-right',
      cellClassName: 'text-right',
      render: (p) => (
        <div className="flex justify-end gap-2">
          <Button type="button" variant="secondary" size="sm" onClick={() => handleOpenEdit(p)}>
            Edit
          </Button>
          <Button type="button" variant="danger" size="sm" onClick={() => setDeleteTarget(p.provider_code)}>
            Delete
          </Button>
        </div>
      ),
    },
  ]

  return (
    <div>
      <PageHeader
        title="Service providers"
        subtitle="Manage LLM service providers used by models"
        actions={
          <Button type="button" onClick={() => setShowCreateModal(true)}>
            <HiPlus className="mr-2 h-4 w-4" />
            Add provider
          </Button>
        }
      />

      <DataTable
        columns={columns}
        data={providers}
        loading={loading}
        emptyTitle="No service providers"
        emptyDescription="Add a provider to connect models to external APIs."
        getRowKey={(p) => p.provider_code}
      />

      <Modal open={showCreateModal} onClose={() => setShowCreateModal(false)} title="Create service provider" size="md">
        <form onSubmit={handleCreate} className="space-y-4">
          <Input
            label="Provider code"
            value={createForm.provider_code}
            onChange={(e) => setCreateForm((p) => ({ ...p, provider_code: e.target.value }))}
            required
            maxLength={50}
            placeholder="e.g. gemini"
          />
          <Input
            label="Provider name"
            value={createForm.provider_name}
            onChange={(e) => setCreateForm((p) => ({ ...p, provider_name: e.target.value }))}
            required
            maxLength={100}
            placeholder="e.g. Gemini"
          />
          <div className="flex justify-end gap-2 border-t border-muted pt-4 dark:border-muted-dark">
            <Button type="button" variant="secondary" onClick={() => setShowCreateModal(false)}>Cancel</Button>
            <Button type="submit" disabled={createLoading}>{createLoading ? 'Creating…' : 'Create'}</Button>
          </div>
        </form>
      </Modal>

      <Modal open={showEditModal && Boolean(selectedProvider)} onClose={() => setShowEditModal(false)} title="Edit service provider" size="md">
        <form onSubmit={handleUpdate} className="space-y-4">
          <p className="text-sm text-text-muted dark:text-text-muted-dark">
            Provider code: <span className="font-mono font-medium text-secondary-900 dark:text-secondary-100">{selectedProvider?.provider_code}</span>
          </p>
          <Input
            label="Provider name"
            value={editForm.provider_name}
            onChange={(e) => setEditForm({ provider_name: e.target.value })}
            required
            maxLength={100}
          />
          <div className="flex justify-end gap-2 border-t border-muted pt-4 dark:border-muted-dark">
            <Button type="button" variant="secondary" onClick={() => setShowEditModal(false)}>Cancel</Button>
            <Button type="submit" disabled={editLoading}>{editLoading ? 'Saving…' : 'Save'}</Button>
          </div>
        </form>
      </Modal>

      <ConfirmDialog
        open={Boolean(deleteTarget)}
        onClose={() => setDeleteTarget(null)}
        onConfirm={handleConfirmDelete}
        title="Delete provider"
        message={`Delete provider “${deleteTarget}”? This cannot be undone.`}
        confirmLabel="Delete"
        loading={deleteLoading}
      />
    </div>
  )
}

export default ServiceProviders
